/**
 * Max Sales Chat Service
 * Adapter for the Max Sales Agent in the Marketplace.
 *
 * Uses a keyword-based intent system as fallback,
 * structured to be easily swapped for real API when available.
 *
 * Interface prepared for when the Max Sales API becomes available:
 * just swap the adapter.
 */

// ──────────────────────────────────────────────
// Intent definitions (5 main)
// ──────────────────────────────────────────────
const INTENTS = [
  {
    id: 'juridico',
    name: 'Legal Agent',
    keywords: ['prazos', 'processos', 'juridico', 'juridica', 'tribunal', 'advog', 'petiç', 'jurisprudencia', 'contrato', 'cláusula', 'direito', 'legal', 'compliance', 'processo judicial', 'audiência', 'mandado', 'liminar'],
    description: 'Manages deadlines, processes, jurisprudence, and legal documents',
    skills: ['Procedural Deadline Management', 'Jurisprudence Search', 'Petition Generation', 'Contract Analysis'],
    links: {
      agent: '/marketplace/juridico',
      skills: '/skills#juridico'
    },
    qualification: [
      { key: 'volume_processos', question: 'How many processes do you manage per month, approximately?' },
      { key: 'tipo_atuacao', question: 'What is your main area of practice?' },
      { key: 'dor_principal', question: 'What consumes most of your time: deadlines, documentation, or research?' }
    ],
    actionButtons: [
      { label: 'View Legal Agent', href: '/marketplace/juridico' },
      { label: 'View Legal Skills', href: '/skills#juridico' }
    ],
    recommendation: 'Based on your profile, the **Legal Agent** from MaxEmployee is the best choice. It automates deadline management, jurisprudence research, petition generation, and contract analysis.'
  },
  {
    id: 'atendimento',
    name: 'Support Agent',
    keywords: ['clientes', 'atendimento', 'suporte', 'customer', 'sac', 'helpdesk', 'chatbot', 'conversão', 'satisfação', 'satisfaction', 'ticket', 'suport'],
    description: 'Serves customers, answers FAQs, and escalates to humans',
    skills: ['Multi-Channel Support', 'Smart FAQ', 'Automatic Escalation', 'Satisfaction Analysis'],
    links: {
      agent: '/marketplace/atendimento',
      skills: '/skills#atendimento'
    },
    qualification: [
      { key: 'canais', question: 'Through which channels do your customers contact you? (WhatsApp, email, phone, social media)' },
      { key: 'volume_tickets', question: 'How many support tickets do you receive per day?' },
      { key: 'tempo_resposta', question: 'What is your current average response time?' }
    ],
    actionButtons: [
      { label: 'View Support Agent', href: '/marketplace/atendimento' },
      { label: 'View Support Skills', href: '/skills#atendimento' }
    ],
    recommendation: 'Based on your data, the **Support Agent** is ideal. It serves via WhatsApp, email, social media, and escalates automatically when needed.'
  },
  {
    id: 'vendas',
    name: 'Sales Agent',
    keywords: ['vendas', 'propostas', 'crm', 'leads', 'negócio', 'negocio', 'oportunidade', 'pipeline', 'orçamento', 'proposta comercial', 'follow-up', 'followup', 'vender', 'comercial'],
    description: 'Qualifies leads, manages pipeline, and generates proposals',
    skills: ['Lead Qualification', 'Pipeline Management', 'Proposal Generation', 'Automated Follow-up'],
    links: {
      agent: '/marketplace/vendas',
      skills: '/skills#vendas'
    },
    qualification: [
      { key: 'time_vendas', question: 'How many people are on your sales team?' },
      { key: 'ticket_medio', question: 'What is the average ticket of your products?' },
      { key: 'volume_leads', question: 'How many new leads do you receive per month?' }
    ],
    actionButtons: [
      { label: 'View Sales Agent', href: '/marketplace/vendas' },
      { label: 'View Sales Skills', href: '/skills#vendas' }
    ],
    recommendation: 'Your lead volume and qualification needs suggest the **Sales Agent**. It qualifies automatically, manages the pipeline, and sends personalized follow-ups.'
  },
  {
    id: 'financeiro',
    name: 'Financial Agent',
    keywords: ['financeiro', 'concentração', 'conciliação', 'conciiação', 'notas', 'finance', 'fatura', 'faturamento', 'pagamento', 'pag', 'cobrança', 'cobranca', 'boleto', 'nfe', 'nf-e', 'imposto', 'contas a pagar', 'contas a receber'],
    description: 'Reconciles payments, issues invoices, and controls cash flow',
    skills: ['Automatic Reconciliation', 'Invoice Issuance', 'Cash Flow Control', 'Collections Management'],
    links: {
      agent: '/marketplace/financeiro',
      skills: '/skills#financeiro'
    },
    qualification: [
      { key: 'volume_nf', question: 'How many invoices do you issue per month?' },
      { key: 'equipe_fin', question: 'How many people work with finance in your company?' },
      { key: 'dor_fin', question: 'What is the biggest financial challenge: reconciliation, invoicing, or collections?' }
    ],
    actionButtons: [
      { label: 'View Financial Agent', href: '/marketplace/financeiro' },
      { label: 'View Financial Skills', href: '/skills#financeiro' }
    ],
    recommendation: 'For your operation, the **Financial Agent** is the most suitable. It automates invoice issuance, payment reconciliation, and collections management.'
  },
  {
    id: 'saude',
    name: 'Health Agent',
    keywords: ['saúde', 'saude', 'pacientes', 'clínica', 'clinica', 'hospital', 'médico', 'medico', 'agendamento', 'consulta', 'prontuário', 'prontuario', 'petição médica', 'convênio', 'convenio'],
    description: 'Manages appointments, medical records, and patient communication',
    skills: ['Smart Scheduling', 'Medical Records Management', 'Patient Communication', 'Clinical Reports'],
    links: {
      agent: '/marketplace/saude',
      skills: '/skills#saude'
    },
    qualification: [
      { key: 'tipo_clinica', question: 'What type is your clinic or practice?' },
      { key: 'volume_pacientes', question: 'How many patients do you see per day?' },
      { key: 'principal_dor', question: 'What consumes the most time: scheduling, records, or communication?' }
    ],
    actionButtons: [
      { label: 'View Health Agent', href: '/marketplace/saude' },
      { label: 'View Health Skills', href: '/skills#saude' }
    ],
    recommendation: 'For your clinic/practice, the **Health Agent** is the ideal choice. It manages appointments, medical records, and automates patient communication.'
  }
];

// ──────────────────────────────────────────────
// Conversation flow states
// ──────────────────────────────────────────────
const FLOW_STATES = {
  GREETING: 'greeting',
  INTENT_RECOGNITION: 'intent_recognition',
  QUALIFICATION: 'qualification',
  RECOMMENDATION: 'recommendation',
  DONE: 'done'
};

class MaxSalesChatService {
  constructor() {
    this.flowState = FLOW_STATES.GREETING;
    this.userData = {};
    this.currentIntent = null;
    this.currentQuestionIndex = 0;
    this.isSimulated = true;
  }

  /**
   * Main entry point — called by ChatWidget when user sends a message
   */
  async sendMessage(message) {
    if (this.isSimulated) {
      return this._handleSimulatedResponse(message);
    }

    // Real API implementation would go here
    // return api.post('/sales/chat', { message, ... });
    throw new Error("Real API not implemented yet");
  }

  async _handleSimulatedResponse(message) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const lowerMsg = message.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    switch (this.flowState) {
      case FLOW_STATES.GREETING:
        this.flowState = FLOW_STATES.INTENT_RECOGNITION;
        return {
          content: "Hello! I'm the MaxEmployee sales assistant. 👋\n\nWhat is your biggest business challenge today? Describe it in one sentence and I'll help you find the ideal solution.",
          stepId: 'greeting'
        };

      case FLOW_STATES.INTENT_RECOGNITION:
        const intent = this._recognizeIntent(lowerMsg);

        if (intent) {
          this.currentIntent = intent;
          this.userData.intent = intent.id;
          this.flowState = FLOW_STATES.QUALIFICATION;
          this.currentQuestionIndex = 0;

          const qual = intent.qualification[0];
          return {
            content: `Perfect! From what you described, I recommend the **${intent.name}** from MaxEmployee.\n\n${intent.description}\n\nNow, to personalize my recommendation, I need to understand your case better.\n\n${qual.question}`,
            stepId: 'qual_1',
            actionButtons: intent.actionButtons.slice(0, 1)
          };
        } else {
          return {
            content: "I understand! To help you better, tell me a little more:\n\n• Which area of your business needs automation? (support, sales, legal, financial, health...)\n• What is the main problem you want to solve?",
            stepId: 'guidance'
          };
        }

      case FLOW_STATES.QUALIFICATION: {
        const intent = this.currentIntent;
        if (intent) {
          const currentQ = intent.qualification[this.currentQuestionIndex];
          if (currentQ) {
            this.userData[currentQ.key] = message;
            this.currentQuestionIndex++;
          }
        }

        if (intent && this.currentQuestionIndex >= intent.qualification.length) {
          this.flowState = FLOW_STATES.RECOMMENDATION;
          return {
            content: `${intent.recommendation}\n\n### Recommended skills for you:\n\n${intent.skills.map(s => `• **${s}**`).join('\n')}\n\nWould you like to schedule a demo or start a free trial?`,
            stepId: 'final_recommendation',
            actionButtons: intent.actionButtons
          };
        }

        const nextQ = intent.qualification[this.currentQuestionIndex];
        if (nextQ) {
          return {
            content: `Great. One last question:\n\n${nextQ.question}`,
            stepId: `qual_${this.currentQuestionIndex + 1}`,
            actionButtons: []
          };
        }
        break;
      }

      case FLOW_STATES.RECOMMENDATION:
        this.flowState = FLOW_STATES.DONE;
        return {
          content: "Thank you very much for the information! 🎉\n\nA MaxEmployee specialist can contact you to show you everything our agent can do for your business.\n\nIn the meantime, explore our marketplace and discover all available solutions.",
          stepId: 'done',
          actionButtons: [
            { label: 'Explore Marketplace', href: '/marketplace' },
            { label: 'Talk to a Specialist', href: '/contact' }
          ]
        };

      default:
        return {
          content: "The conversation has been ended. Come back whenever you need!",
          stepId: 'end'
        };
    }

    return {
      content: "Thank you! We are processing your response.",
      stepId: 'unknown'
    };
  }

  /**
   * Recognize user intent from message keywords
   * Uses fuzzy matching (strip accents, lowercase)
   */
  _recognizeIntent(message) {
    let bestIntent = null;
    let bestScore = 0;

    for (const intent of INTENTS) {
      let score = 0;
      for (const keyword of intent.keywords) {
        if (message.includes(keyword.toLowerCase())) {
          score++;
        }
      }
      if (score > bestScore) {
        bestScore = score;
        bestIntent = intent;
      }
    }

    return bestScore > 0 ? bestIntent : null;
  }

  /**
   * Reset the entire conversation flow
   */
  reset() {
    this.flowState = FLOW_STATES.GREETING;
    this.userData = {};
    this.currentIntent = null;
    this.currentQuestionIndex = 0;
  }
}

export const maxSalesChat = new MaxSalesChatService();

export { INTENTS };
